import { __awaiter, __decorate, __rest } from "tslib";
import { action, computed, flow, flowResult, makeAutoObservable, makeObservable, observable } from "mobx";
import { getImageDimensions, isPlaceholderImage, storeCorsImage, storeFileImage, storeImage } from "../utilities/image-utils";
import { DEBUG_IMAGES } from "../lib/debug";
import placeholderImage from "../assets/image_placeholder.png";
import { getAssetUrl } from "../utilities/asset-utils";
import { urlParams } from "../utilities/url-params";
import { getRawCurriculumUrl } from "../../authoring-api/src/helpers/github";
export const kExternalUrlHandlerName = "externalUrl";
export const kLocalAssetsHandlerName = "localAssets";
export const kFirebaseStorageHandlerName = "firebaseStorage";
export const kFirebaseRealTimeDBHandlerName = "firebaseRealTimeDB";
export var EntryStatus;
(function (EntryStatus) {
    EntryStatus["PendingStorage"] = "pendingStorage";
    EntryStatus["PendingDimensions"] = "pendingDimensions";
    EntryStatus["Ready"] = "ready";
    EntryStatus["Error"] = "error";
})(EntryStatus || (EntryStatus = {}));
export class ImageMapEntry {
    constructor() {
        Object.defineProperty(this, "filename", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "contentUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "displayUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: placeholderImage
        });
        Object.defineProperty(this, "width", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "height", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "status", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: EntryStatus.PendingStorage
        });
        Object.defineProperty(this, "retries", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        makeAutoObservable(this);
    }
    toJSON() {
        // When a class instance is spread like this, only properties that have been assigned
        // get included in the result. However in typescript the resulting type is `this`
        // instead of just the properties. This is why the function is explicitly typed.
        return Object.assign({}, this);
    }
    update(props) {
        Object.assign(this, props);
    }
    static create(props) {
        const newEntry = (new ImageMapEntry());
        newEntry.update(props);
        return newEntry;
    }
}
export class ImageMap {
    constructor() {
        Object.defineProperty(this, "images", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map()
        });
        Object.defineProperty(this, "unitCodeMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map()
        });
        Object.defineProperty(this, "unitUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "handlers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "storingPromises", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "curriculumUrlOverride", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_db", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        makeObservable(this);
        // placeholder doesn't have contentUrl
        this._addImage(placeholderImage, { displayUrl: placeholderImage, success: true });
        // when in authoring preview mode load images from the raw branch url
        // so we don't have to wait for deploys to see image changes
        if (urlParams.authoringBranch) {
            this.curriculumUrlOverride = getRawCurriculumUrl(urlParams.authoringBranch);
        }
        this.registerHandler(firebaseRealTimeDBImagesHandler);
        this.registerHandler(firebaseStorageImagesHandler);
        this.registerHandler(localAssetsImagesHandler);
        this.registerHandler(externalUrlImagesHandler);
    }
    isImageUrl(url) {
        return !!this.getHandler(url);
    }
    hasImage(url) {
        return this.images.has(url);
    }
    getHandler(url) {
        const index = this.handlers.findIndex(handler => handler.match(url));
        return index >= 0 ? this.handlers[index] : undefined;
    }
    isExternalUrl(url) {
        const index = this.handlers.findIndex(handler => handler.match(url));
        return (index >= 0) &&
            (this.handlers[index].name === kExternalUrlHandlerName);
    }
    isPlaceholder(url) {
        return isPlaceholderImage(url);
    }
    clonePlaceholder() {
        return ImageMapEntry.create(this.images.get(placeholderImage).toJSON());
    }
    getCachedImage(url) {
        return url ? this.images.get(url) : undefined;
    }
    get curriculumUrl() {
        if (this.curriculumUrlOverride) {
            return this.curriculumUrlOverride;
        }
        if (!this.unitUrl)
            return;
        return (new URL("../", this.unitUrl)).href;
    }
    registerHandler(handler) {
        handler.imageMap = this;
        this.handlers.push(handler);
        this.handlers.sort((a, b) => {
            return (b.priority || 0) - (a.priority || 0);
        });
    }
    /**
     * If an entry already exists at that url, update it.
     * Otherwise create a new entry from the snapshot and store it at the url
     * @param url
     * @param entry
     */
    _addOrUpdateEntry(url, entrySnapshot) {
        const existingEntry = this.images.get(url);
        if (existingEntry) {
            existingEntry.update(entrySnapshot);
            return existingEntry;
        }
        else {
            const newEntry = new ImageMapEntry();
            newEntry.update(entrySnapshot);
            this.images.set(url, newEntry);
            return newEntry;
        }
    }
    /**
     * If the contentUrl of the entry is different than its url (key) create or update
     * the entry at contentUrl. This gets complicated when the two entries have different
     * statuses. See image-map.md "URL Conversion" for more details.
     */
    _syncContentUrl(url, entry) {
        if (!entry.contentUrl || (url === entry.contentUrl)) {
            return;
        }
        const entrySnapshot = entry.toJSON();
        const existingEntry = this.images.get(entry.contentUrl);
        if (!existingEntry || existingEntry.status === EntryStatus.Error) {
            if (entry.status === EntryStatus.Ready) {
                // store or update the entry
                this._addOrUpdateEntry(entry.contentUrl, entrySnapshot);
            }
            else if (entry.status === EntryStatus.PendingDimensions) {
                // store or update the entry
                this._addOrUpdateEntry(entry.contentUrl, entrySnapshot);
                // copy the storing promise incase some code calls
                // getImage(entry.contentUrl)
                this.storingPromises[entry.contentUrl] = this.storingPromises[url];
            }
        }
        if ((existingEntry === null || existingEntry === void 0 ? void 0 : existingEntry.status) === EntryStatus.PendingDimensions &&
            (entry.status === EntryStatus.Error || entry.status === EntryStatus.Ready) &&
            this.storingPromises[url] === this.storingPromises[entry.contentUrl]) {
            // If the existingEntry is "managed" by the same promise as the entry
            // we should updated it in some cases.
            // See image-map.md "Updated Cache entry is in the Error state" and
            // "Updated cache entry is in Ready state"
            this._addOrUpdateEntry(entry.contentUrl, entrySnapshot);
        }
    }
    setUnitUrl(url) {
        this.unitUrl = url;
    }
    setUnitCodeMap(map) {
        this.unitCodeMap.replace(map);
    }
    // Flows are the recommended way to deal with async actions in MobX and MobX State Tree.
    // However, typing them is difficult. If you leave them untyped like:
    // `flow(function* addImage(url: string, snapshot: ImageMapEntrySnapshot) {`
    // The return value will be a Promise based on the actual return value of the generator
    // function. This means there is no enforcement of a particular return type.
    // Also the value of all yield calls is any.
    //
    // The approach used below types the generator function with:
    //   Generator<PromiseLike<any>, ImageMapEntryType, unknown>
    // which means:
    // - the value passed to yield has to be PromiseLike<any>
    // - the return type has to be ImageMapEntryType
    // - yield returns an unknown type so you have to cast it before using it
    //
    // Ignoring the return value of yield there are two other options for enforcing the return
    // value of the flow:
    // - `flow<ImageMapEntryType, any>(function* addImage(url: string, snapshot: ImageMapEntrySnapshot) {`
    // - `flow<ImageMapEntryType, [string, ImageMapEntrySnapshot]>(function* addImage(url, snapshot) {`
    // The first option looks nice because the arguments are typed as normal, however it means the actual
    // action will have un typed arguments
    // The second results in a weird signature where the argument names of addImage are just arg0, arg1
    // In both of these cases the return value of yield is any so it is not as safe as the Generator
    // approach being used.
    //
    // There is also the yield* toGenerator approach https://mobx-state-tree.js.org/API/#togenerator which
    // provides a way to automatically type the return value of the yield. But that doesn't solve
    // the problem of typing the return value of the flow.
    *_addImage(url, storeResult) {
        // Update or add the entry. We do this whether there is an error or not.
        // If there is an error it is still recorded so observers of the entry
        // will see the change
        const existingEntry = this.images.get(url);
        let retries = 0;
        // If there is an existing entry we copy the retries property
        if (existingEntry) {
            retries = existingEntry.retries;
        }
        const { success: successfulStore } = storeResult, otherProps = __rest(storeResult, ["success"]);
        const entry = this._addOrUpdateEntry(url, Object.assign(Object.assign({}, otherProps), { status: successfulStore ? EntryStatus.PendingDimensions : EntryStatus.Error, width: undefined, height: undefined, retries }));
        if (entry.status === EntryStatus.Error) {
            // This means the storage operation failed.
            // We could clear the storingPromise here, but instead we just leave it and
            // rely on getImage to ignore the storingPromise when it sees there is
            // an entry with a status of error.
            // Even if this entry has a contentUrl that is different than its url
            // we do not update the entry at the contentUrl.
            // See image-map.md "Updated cache entry is in the Error state"
            // Note: when there is an error at this point the promise that is managing
            // this call to _addImage should not be responsible for the entry at
            // contentUrl.
            // We return so we don't sync and don't try to get the dimensions
            // See image-map.md "Dimensions" for why we don't set the dimensions
            return entry;
        }
        this._syncContentUrl(url, entry);
        try {
            // If the getImageDimension image element never loads or errors then we won't get
            // past this line. However I'd hope that the browser will eventually trigger
            // one of those events.
            // In most cases _addImage is not called until the image has already been
            // downloaded and displayUrl is actually a blob url.
            // So it should be unlikely in these cases that getImageDimensions will fail.
            //
            // We know the entry.displayUrl is defined because we made sure the snapshot.displayUrl
            // is defined above.
            const dimensions = (yield getImageDimensions(entry.displayUrl));
            entry.width = dimensions.width;
            entry.height = dimensions.height;
            entry.status = EntryStatus.Ready;
        }
        catch (error) {
            entry.status = EntryStatus.Error;
            // If there is a contentUrl there could be a second entry that needs to be updated.
            // syncContentUrl takes care of this
            // Note: we are not updating or clearing the other fields of the entry here
            // Its status will be Error, but it might have a contentUrl and a displayUrl.
            // Leaving the contentUrl in place is necessary so syncContentUrl can work.
            // Leaving displayUrl untouched might not be the best thing to do, but there
            // isn't a good reason to change it so far.
        }
        this._syncContentUrl(url, entry);
        return entry;
    }
    initialize(db) {
        this._db = db;
    }
    // This doesn't support the observer style pattern.
    // We don't have a URL to send to getCachedImage after we call
    // addFileImage. We'd have to do quite a bit of work to support returning
    // an ImageMapEntry immediately since it wouldn't have a key to be added into the
    // map until it has been uploaded.
    // The image map entries do have a state of pending storage, so this is the state
    // the entry should be in.
    addFileImage(file) {
        return __awaiter(this, void 0, void 0, function* () {
            const simpleImage = yield storeFileImage(this._db, file);
            const { normalized } = parseFauxFirebaseRTDBUrl(simpleImage.imageUrl);
            const entry = {
                filename: file.name,
                contentUrl: normalized,
                displayUrl: simpleImage.imageData,
                success: true
            };
            return flowResult(this._addImage(entry.contentUrl, entry));
        });
    }
    storeAndAddImage(url, handler, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const storeResult = yield handler.store(url, Object.assign({ db: this._db }, options));
            return flowResult(this._addImage(url, storeResult));
        });
    }
    getImage(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!url) {
                // TODO: how often does this happen, should it be silently ignored like this?
                console.warn("ImageMap#getImage called with a falsy URL", url);
                return this.clonePlaceholder();
            }
            const imageEntry = this.images.get(url);
            if ((imageEntry === null || imageEntry === void 0 ? void 0 : imageEntry.status) === EntryStatus.Ready) {
                // This image has been downloaded successfully already
                return imageEntry;
            }
            const existingStoringPromise = this.storingPromises[url];
            if (existingStoringPromise &&
                ((imageEntry === null || imageEntry === void 0 ? void 0 : imageEntry.status) !== EntryStatus.Error || (imageEntry === null || imageEntry === void 0 ? void 0 : imageEntry.retries) >= 2)) {
                // If the imageEntry is errored we ignore the existing promise
                // This way a second getImage request will try to store the image again
                // If we've already retried storing the image 2 times, don't try again.
                return existingStoringPromise;
            }
            const handler = this.getHandler(url);
            if (!handler) {
                console.warn(`No handler found for ${url}`);
                return this.clonePlaceholder();
            }
            // If there is an existing entry we'll overwrite it so its status is
            // `PendingStorage` and the `displayUrl` is the placeholder. In theory the
            // existingEntry could have a status of PendingStorage, PendingDimensions, or
            // Error. Because there is no existingStoringPromise the status should
            // really not be PendingStorage or PendingDimensions.
            if ((imageEntry === null || imageEntry === void 0 ? void 0 : imageEntry.status) === EntryStatus.PendingStorage ||
                (imageEntry === null || imageEntry === void 0 ? void 0 : imageEntry.status) === EntryStatus.PendingDimensions) {
                console.warn(`ImageMap.getImage found an entry with a status ${imageEntry.status} at ${url}`);
            }
            let retries = 0;
            if (imageEntry) {
                // When we have an imageEntry at this point it means we are retrying
                retries = imageEntry.retries + 1;
            }
            this._addOrUpdateEntry(url, { status: EntryStatus.PendingStorage, displayUrl: placeholderImage, retries });
            const storingPromise = this.storeAndAddImage(url, handler, options);
            // keep track of the storingPromise
            this.storingPromises[url] = storingPromise;
            return storingPromise;
        });
    }
    getImageEntry(url, options) {
        if (!url) {
            console.warn("ImageMap#getImageEntry called with a falsy URL", url);
            return undefined;
        }
        this.getImage(url, options);
        return this.getCachedImage(url);
    }
}
__decorate([
    observable
], ImageMap.prototype, "unitUrl", void 0);
__decorate([
    computed
], ImageMap.prototype, "curriculumUrl", null);
__decorate([
    action
], ImageMap.prototype, "registerHandler", null);
__decorate([
    action
], ImageMap.prototype, "_addOrUpdateEntry", null);
__decorate([
    action
], ImageMap.prototype, "_syncContentUrl", null);
__decorate([
    action
], ImageMap.prototype, "setUnitUrl", null);
__decorate([
    action
], ImageMap.prototype, "setUnitCodeMap", null);
__decorate([
    flow
], ImageMap.prototype, "_addImage", null);
__decorate([
    action
], ImageMap.prototype, "initialize", null);
__decorate([
    action
], ImageMap.prototype, "addFileImage", null);
__decorate([
    action
], ImageMap.prototype, "storeAndAddImage", null);
__decorate([
    action
], ImageMap.prototype, "getImage", null);
/*
 * externalUrlImagesHandler
 */
export const externalUrlImagesHandler = {
    name: kExternalUrlHandlerName,
    priority: 1,
    match(url) {
        return url ? /^(https?:\/\/|data:image\/)/.test(url) : false;
    },
    store(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const { db } = options || {};
            // upload images from external urls to our own firebase if possible
            // this may fail depending on CORS settings on target image.
            // After the data of the image is uploaded to firebase, it is not
            // explicitly downloaded again:
            // 1. storeImage creates a canvas and adds the url to this canvas
            // 2. a data uri is extracted from the canvas
            // 3. an object with the data uri is uploaded to firebase
            // 4. the data uri is "fetched" to turn it into a blob and then blob url
            // 5. the resulting imageData value will be the blob url
            // 6. the displayUrl is set to this blob url
            // In the context of authoring, db.stores is undefined and we consequently
            // do not upload any images added via the authoring system.
            if ((_a = db === null || db === void 0 ? void 0 : db.stores) === null || _a === void 0 ? void 0 : _a.user.id) {
                try {
                    const simpleImage = yield storeImage(db, url);
                    if (isPlaceholderImage(simpleImage.imageUrl)) {
                        // conversion errors are resolved to placeholder image
                        // this generally occurs due to a CORS error, in which
                        // case we just use the original url.
                        //
                        // TODO: if we get a CORS error we'll likely get that same
                        // error again. So we should serialize that info and use it
                        // to not try to download the image again.
                        return { contentUrl: url, displayUrl: url, success: true };
                    }
                    else {
                        const { normalized } = parseFauxFirebaseRTDBUrl(simpleImage.imageUrl);
                        return { contentUrl: normalized, displayUrl: simpleImage.imageData,
                            success: true };
                    }
                }
                catch (error) {
                    // If the silent upload has failed, do we retain the full url or
                    // encourage the user to download a copy and re-upload?
                    // For now, return the original image url.
                    return { contentUrl: url, displayUrl: url, success: true };
                }
            }
            else {
                // For now, return the original image url.
                return { contentUrl: url, displayUrl: url, success: true };
            }
        });
    },
    imageMap: {}
};
/*
 * localAssetsImagesHandler
 */
export const localAssetsImagesHandler = {
    name: kLocalAssetsHandlerName,
    priority: 2,
    match(url) {
        // don't match values with a protocol or port specified
        return !/:/.test(url) &&
            // or legacy firebase storage references
            !/^\/.+\/portals\/.+$/.test(url) &&
            // spaces are not allowed
            !/ /.test(url) &&
            // make sure there's at least one slash
            /\//.test(url) &&
            // make sure value ends with a file extension
            /\.[a-z]{3,}$/i.test(url);
    },
    store(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            // Legacy curriculum units lived in the defunct "curriculum" directory in subfolders
            // named after the unit title, e.g. "curriculum/stretching-and-shrinking". References
            // to files in those directories need to be converted to use the new file structure
            // where the unit code is the directory name, e.g. "sas".
            const urlPieces = url.match(/curriculum\/([^/]+)\/(.*)/);
            let _url = url;
            if (urlPieces && this.imageMap.unitCodeMap) {
                const urlUnitDir = urlPieces[1];
                const newUnitDir = this.imageMap.unitCodeMap.get(urlUnitDir) || urlUnitDir;
                _url = `${newUnitDir}/${urlPieces[2]}`;
            }
            // We also need to convert legacy drawing tool stamp paths
            _url = _url.replace("assets/tools/drawing-tool/stamps", "msa/stamps");
            // If curriculumUrl is defined and the image isn't in CLUE's own assets directory,
            // build an absolute URL for the displayUrl.
            const displayUrl = this.imageMap.curriculumUrl && !/^assets\//.test(_url)
                ? new URL(_url, this.imageMap.curriculumUrl).href
                : getAssetUrl(_url);
            return { filename: options === null || options === void 0 ? void 0 : options.filename, contentUrl: _url, displayUrl, success: true };
        });
    },
    imageMap: {}
};
/*
 * firebaseStorageImagesHandler
 */
const kFirebaseStorageUrlPrefix = "https://firebasestorage.googleapis.com";
// The contentUrl is not set here.
// This means any content that is referencing an image that cannot be downloaded
// will not be updated. Modifying content like this seems kind of dangerous because
// this could be a temporary network error.
// By not setting the contentUrl, it also means that the default placeholder image
// entry will not be modified by syncContentUrl. That is a good thing.
const kErrorStorageResult = {
    displayUrl: placeholderImage, success: false
};
export const firebaseStorageImagesHandler = {
    name: kFirebaseStorageHandlerName,
    priority: 3,
    match(url) {
        return url.startsWith(kFirebaseStorageUrlPrefix) ||
            // original firebase storage path reference
            /^\/.+\/portals\/.+$/.test(url);
    },
    store(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { db } = options || {};
            // All images from firebase storage must be migrated to realtime database
            const isStorageUrl = url.startsWith(kFirebaseStorageUrlPrefix);
            const storageUrl = url.startsWith(kFirebaseStorageUrlPrefix) ? url : undefined;
            const storagePath = !isStorageUrl ? url : undefined;
            // Pass in the imagePath as the second argument to get the ref to firebase storage by url
            // This is needed if an image of the same name has been uploaded in two different components,
            // since each public URL becomes invalid and a new url generated on upload
            if (db === null || db === void 0 ? void 0 : db.stores.user.id) {
                try {
                    const newUrl = yield db.firebase.getPublicUrlFromStore(storagePath, storageUrl);
                    if (newUrl) {
                        const simpleImage = yield storeCorsImage(db, newUrl);
                        // Image has been retrieved from Storage, now we can safely remove the old image
                        // TODO: remove old images
                        const { normalized } = parseFauxFirebaseRTDBUrl(simpleImage.imageUrl);
                        return { contentUrl: normalized, displayUrl: simpleImage.imageData,
                            success: true };
                    }
                    else {
                        return kErrorStorageResult;
                    }
                }
                catch (error) {
                    return kErrorStorageResult;
                }
            }
            else {
                return kErrorStorageResult;
            }
        });
    },
    imageMap: {}
};
/*
 * firebaseRealTimeDBImagesHandler
 */
const kCCImageScheme = "ccimg";
const kFirebaseRTDBFauxHost = "fbrtdb.concord.org";
const kFirebaseRTDBEscFauxHost = kFirebaseRTDBFauxHost.replace(/\./g, "\\.");
const kFirebaseRTDBFauxUrlPrefix = `${kCCImageScheme}://${kFirebaseRTDBFauxHost}`;
const kFirebaseRTDBFauxUrlRegex = new RegExp(`^${kCCImageScheme}://(${kFirebaseRTDBEscFauxHost}/)?(.*)`);
function createFirebaseRTDBFauxUrl(path) {
    return `${kFirebaseRTDBFauxUrlPrefix}/${path}`;
}
function extractPathFromFirebaseRTDBFauxUrl(url) {
    const match = kFirebaseRTDBFauxUrlRegex.exec(url);
    return match && match[2] || undefined;
}
function parseImagePath(path) {
    const match = /([^/]+)\/([^/]+)/.exec(path);
    const classHash = (match === null || match === void 0 ? void 0 : match[1]) || undefined;
    const imageKey = (match === null || match === void 0 ? void 0 : match[2]) || undefined;
    return { classHash, imageKey };
}
function parseFauxFirebaseRTDBUrl(url) {
    const path = extractPathFromFirebaseRTDBFauxUrl(url) || undefined;
    const { classHash, imageKey } = path ? parseImagePath(path) : {};
    const normalized = path && createFirebaseRTDBFauxUrl(path);
    return { path, classHash, imageKey, normalized };
}
export const firebaseRealTimeDBImagesHandler = {
    name: kFirebaseRealTimeDBHandlerName,
    priority: 4,
    match(url) {
        return url.startsWith(kFirebaseRTDBFauxUrlPrefix) ||
            (url.startsWith(`${kCCImageScheme}://`) && (url.indexOf("concord.org") < 0));
    },
    store(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { db } = options || {};
            const { path, classHash, imageKey, normalized } = parseFauxFirebaseRTDBUrl(url);
            if (db && path && normalized) {
                // In theory we could direct all firebase image requests to the cloud function,
                // but only cross-class supports require the use of the cloud function.
                const blobUrl = classHash !== db.stores.user.classHash
                    ? yield db.getCloudImageBlob(normalized)
                    : yield db.getImageBlob(imageKey);
                return blobUrl
                    ? {
                        filename: options === null || options === void 0 ? void 0 : options.filename, contentUrl: normalized, displayUrl: blobUrl,
                        success: true
                    }
                    // Note: we used to return an empty image entry here. This used to cause
                    // problems with some code that would then try to load the original url which
                    // might be a ccimg: url.
                    // This empty entry seems to also be expected by jxg-image which was testing
                    // for falsey displayUrl. It has been updated to also check the status of the
                    // entry
                    : kErrorStorageResult;
            }
            else {
                return kErrorStorageResult;
            }
        });
    },
    imageMap: {}
};
export const gImageMap = new ImageMap();
if (DEBUG_IMAGES) {
    window.imageMap = gImageMap;
}
//# sourceMappingURL=data:application/json;base64,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